/**
 * Career-import enrichment queue — imported surgeries still missing technique steps.
 * Used by the dashboard CTA and the Library `enrich=1` filter.
 */
import type { Surgery } from '@/lib/types';

/** True when the case already has at least one non-empty technique step. */
export function hasTechniqueSteps(surgery: Surgery): boolean {
  return (surgery.pasos?.some((p) => p.trim()) ?? false);
}

/**
 * Imported skeleton that still needs pasos filled (manually or via AI).
 * Only `origen: 'import'` — manually logged cases without pasos stay out of the queue.
 */
export function needsEnrichment(surgery: Surgery): boolean {
  return surgery.origen === 'import' && !hasTechniqueSteps(surgery);
}

/** All archive rows waiting to be enriched, newest first. */
export function enrichmentQueue(surgeries: Surgery[]): Surgery[] {
  return surgeries
    .filter(needsEnrichment)
    .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.folio.localeCompare(a.folio));
}

export interface EnrichmentQueueSummary {
  count: number;
  /** Distinct procedure names still missing pasos. */
  procedureCount: number;
  procedures: string[];
}

export function enrichmentQueueSummary(surgeries: Surgery[]): EnrichmentQueueSummary {
  const queue = enrichmentQueue(surgeries);
  const seen = new Map<string, string>();
  for (const s of queue) {
    const key = s.categoria.trim().toLowerCase();
    if (!seen.has(key)) seen.set(key, s.categoria.trim());
  }
  const procedures = [...seen.values()].sort((a, b) => a.localeCompare(b));
  return {
    count: queue.length,
    procedureCount: procedures.length,
    procedures,
  };
}
